import { useEffect, useState } from "react";
import { api, formatCents } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { PageHeader, Card, Button, Stat, Loader, Pill, Select } from "@/components/ui/Primitives";
import { Download, TrendingUp, DollarSign, Receipt, FileText, Building2 } from "lucide-react";
import { toast } from "sonner";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [taxProfile, setTaxProfile] = useState(null);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, tp] = await Promise.all([
          api.get("/reports/landlord/summary"),
          api.get("/reports/tax/profile").catch(() => ({ data: null })),
        ]);
        setSummary(s.data);
        setTaxProfile(tp.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const dl = async (path, filename) => {
    try {
      await downloadFile(path, filename);
    } catch (e) {
      // The downloadFile helper already shows the toast on failure with a
      // proper status-based message (401/402/403/404). For 400 from tax
      // exports the backend means "no records for this year" — surface a
      // friendlier message.
      if (e?.status === 400) {
        toast.error(`No ${filename.split("-")[0] || "tax"} records found for ${year}.`);
      }
    }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Reports & tax"
        subtitle="Portfolio performance and Canadian tax-ready exports."
        actions={
          <>
            <Select value={year} onChange={(e) => setYear(e.target.value)} className="w-28" testid="report-year-select">
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </Select>
            <Button variant="secondary" onClick={() => dl(`/reports/landlord/export.csv?year=${year}`, `landlord-summary-${year}.csv`)} data-testid="export-summary-csv">
              <Download className="w-4 h-4"/> Summary CSV
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="report-stat-income" label="Total income (YTD)" value={formatCents(summary?.total_income || 0)} sub="Rent + fees collected" icon={DollarSign} accent="text-green-800"/>
        <Stat testid="report-stat-expenses" label="Total expenses" value={formatCents(summary?.total_expenses || 0)} sub="Logged this period" icon={Receipt}/>
        <Stat testid="report-stat-net" label="Net income" value={formatCents((summary?.total_income || 0) - (summary?.total_expenses || 0))} sub="Before tax" icon={TrendingUp} accent={(summary?.total_income || 0) - (summary?.total_expenses || 0) >= 0 ? "text-green-800" : "text-red-700"}/>
        <Stat testid="report-stat-properties" label="Active properties" value={summary?.properties_count || 0} sub={`${summary?.units_total || 0} total units`} icon={Building2}/>
      </div>

      <div className="mt-7 grid lg:grid-cols-2 gap-5">
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">T776 — Statement of Real Estate Rentals</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md">For Canadian landlords. Export your rental income statement formatted for the T776 schedule.</p>
            </div>
            <Pill tone="green">CRA</Pill>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={() => dl(`/reports/tax/t776.pdf?year=${year}`, `T776-${year}.pdf`)} data-testid="download-t776">
              <Download className="w-4 h-4"/> Download T776 ({year})
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">NR4 — Non-resident withholding</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md">For non-resident landlords with rental income from Canadian properties. 25% default withholding.</p>
            </div>
            <Pill tone="blue">Non-resident</Pill>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/tax/nr4.pdf?year=${year}`, "_blank", "noopener")} data-testid="download-nr4">
              <Download className="w-4 h-4"/> NR4 ({year}) · Print / Save PDF
            </Button>
            {taxProfile?.non_resident && (
              <span className="text-sm text-slate-600">Withholding: <b>{taxProfile.withholding_pct || 25}%</b></span>
            )}
          </div>
        </Card>
      </div>

      <Card className="mt-5 p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-slate-400"/> By property breakdown</h3>
        <p className="text-sm text-slate-500">Per-property income & expense totals.</p>
        {summary?.by_property?.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 font-semibold">Property</th>
                  <th className="px-3 py-2 font-semibold text-right">Income</th>
                  <th className="px-3 py-2 font-semibold text-right">Expenses</th>
                  <th className="px-3 py-2 font-semibold text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_property.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-3 font-semibold text-slate-900">{row.name}</td>
                    <td className="px-3 py-3 text-right">{formatCents(row.income || 0)}</td>
                    <td className="px-3 py-3 text-right">{formatCents(row.expenses || 0)}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-900">{formatCents((row.income || 0) - (row.expenses || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 text-sm text-slate-500 py-6 text-center">No property data available.</div>
        )}
      </Card>
    </div>
  );
}
